import {
  FilterStore,
  RangeFilter,
  SHOW_HIDDEN_FILTER_KEY,
  SHOW_HIDDEN_FILTER_VALUE,
  ValueFilter,
  isShowHiddenActive,
} from 'components/Filters/FilterStore'
import Checkbox from '../../Checkbox/checkbox'
import { useEffect } from 'react'
import { PiEye, PiEyeSlash } from 'react-icons/pi'
import { useRegisterFilterKey } from '../components/FilterModal'

interface QuickFilterProps {
  title: string
  valueLabel: string
  filterKey: string
  filterStore: FilterStore
}

interface QuickRangeFilterProps extends QuickFilterProps {
  predefinedValue: RangeFilter
}

export function QuickRangeFilter({
  title,
  valueLabel,
  filterKey,
  predefinedValue,
  filterStore,
}: QuickRangeFilterProps) {
  const {
    pendingRangeFilters: pending,
    setPendingRangeFilters: setPending,
    clearPendingRangeFilter: clearPending,
    registerFilterTitle,
  } = filterStore()

  const isActiveValueEqual = (a: RangeFilter, b: RangeFilter) => {
    if (!a || !b) return false
    return a.max == b.max && a.min == b.min
  }

  const isChecked: boolean = isActiveValueEqual(
    pending[filterKey],
    predefinedValue
  )

  const removeFilter = () => {
    clearPending(filterKey)
  }

  const addFilter = () => {
    setPending(filterKey, predefinedValue.min, predefinedValue.max)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  return (
    <QuickFilter
      label={title}
      valueLabel={valueLabel}
      isChecked={isChecked}
      addFilter={addFilter}
      removeFilter={removeFilter}
    />
  )
}

interface QuickValueFilterProps extends QuickFilterProps {
  predefinedValue: ValueFilter[]
}

export function QuickValueFilter({
  title,
  valueLabel,
  filterKey,
  predefinedValue,
  filterStore,
}: QuickValueFilterProps) {
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = filterStore()

  const isActiveValueEqual = (a: ValueFilter[], b: ValueFilter[]) => {
    if (!a || !b) return false
    return a.length === b.length && a.every((v, i) => v.value == b[i].value)
  }

  const isChecked: boolean = isActiveValueEqual(
    pending[filterKey],
    predefinedValue
  )

  const removeFilter = () => {
    clearPending(filterKey)
  }

  const addFilter = () => {
    setPending(filterKey, predefinedValue)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  return (
    <QuickFilter
      label={title}
      valueLabel={valueLabel}
      isChecked={isChecked}
      addFilter={addFilter}
      removeFilter={removeFilter}
    ></QuickFilter>
  )
}

/**
 * FIR-7845 quick filter that flips a table between visible-only (default)
 * and hidden-only rows. Stored as a regular value filter under
 * `show_hidden`, so it hits the wire as `show_hidden=true`, renders as a
 * removable FilterChip, and participates in clear-all, saved views, and
 * apply's page-to-1 reset. Available to every user — only the hide/unhide
 * write action is admin-gated.
 */
export function QuickShowHiddenFilter({
  filterStore,
}: {
  filterStore: FilterStore
}) {
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = filterStore()

  const isChecked = isShowHiddenActive(pending)

  useEffect(() => {
    registerFilterTitle(SHOW_HIDDEN_FILTER_KEY, 'Show Hidden')
  }, [])

  useRegisterFilterKey(SHOW_HIDDEN_FILTER_KEY)

  return (
    <QuickFilter
      label="Show Hidden"
      valueLabel=""
      icon={
        isChecked ? (
          <PiEye className="size-4 shrink-0 text-neutral-500" />
        ) : (
          <PiEyeSlash className="size-4 shrink-0 text-neutral-500" />
        )
      }
      isChecked={isChecked}
      addFilter={() =>
        setPending(SHOW_HIDDEN_FILTER_KEY, SHOW_HIDDEN_FILTER_VALUE)
      }
      removeFilter={() => clearPending(SHOW_HIDDEN_FILTER_KEY)}
    />
  )
}

function QuickFilter({
  label,
  valueLabel,
  isChecked,
  addFilter,
  removeFilter,
  icon,
}: {
  label: string
  valueLabel: string
  isChecked: boolean
  addFilter: () => void
  removeFilter: () => void
  /** Optional leading icon rendered between the checkbox and the label. */
  icon?: React.ReactNode
}) {
  const onChange = () => {
    if (isChecked) {
      removeFilter()
    } else {
      addFilter()
    }
  }

  return (
    <label
      htmlFor={label}
      className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm text-neutral-700 hover:bg-neutral-100"
    >
      <Checkbox id={label} checked={isChecked} onChange={onChange} />
      {icon}
      <span className="select-none">
        {label}
        {valueLabel ? (
          <span className="text-neutral-500">: {valueLabel}</span>
        ) : null}
      </span>
    </label>
  )
}
